import { SegmentEffects } from '@opengov/app-core/analytics';
import { selectCurrentEntity } from '@opengov/app-core/entity';
import { createStructuredSelector } from '@opengov/compose-app/reselect';
import {
  createSavedView as apiCreateSavedView,
  createSavedViewPreview,
  deleteSavedViewById,
  getReportConfigurationById,
  savedViewsByReportId,
  syncEmbedBySavedViewId,
  updateSavedViewById
} from '@opengov/api-deprecated';
import {
  AccountSummaryChartType,
  EDataManagementSemanticTypes,
  EReportChartType,
  IReportConfiguration
} from '@opengov/reporting-types';
import { createSavedView, ISavedView } from '@opengov/reporting-types/src/reportSavedView';
import { produce } from 'immer';

import { composeEffects } from '../../../../module';
import { reportHistoryActionCreators } from '../../history/state';
import { ISavedViewTitleAndDescription } from '../../modals/components/SavedViewCreatingModal/SavedViewCreatingModal';
import { selectCurrentReport } from '../../report/selectors/reportSelectors';
import { resolveReportConfigurationId } from '../../reportConfiguration/resolveReportConfigurationId';
import { selectCurrentReportConfiguration } from '../../reportConfiguration/selectors';
import { selectSavedViews } from '../selectors';
import { savedViewActionCreators } from '../state';

const { loadSavedViewsError, loadSavedViewsSuccess, queueSavedViewEmbed, dequeueSavedViewEmbed } =
  savedViewActionCreators;

export const isUsingExtendedColumns = (reportConfiguration: IReportConfiguration): boolean => {
  if (!reportConfiguration) {
    return false;
  }

  const tableQuery = reportConfiguration.detailTableQuery;
  const vizQuery = reportConfiguration.visualizationQuery;

  const storedAsColumnNames = tableQuery.filter
    .map((filter) => filter.columnName)
    .concat(vizQuery.filter.map((filter) => filter.columnName))
    .concat(tableQuery.sortColumn);

  const storedAsColumns = [vizQuery.organizedBy.column, vizQuery.showColumn, vizQuery.categorizedByColumn];

  return storedAsColumnNames
    .map((name) => tableQuery.columns.find((column) => column.name === name))
    .concat(storedAsColumns)
    .some(
      (column) =>
        column &&
        (column as any).semanticTypes &&
        (column as any).semanticTypes.some((type) => type === EDataManagementSemanticTypes.extendedSchema)
    );
};

export const savedViewEffects = composeEffects('savedViews', (compose) => ({
  loadSavedViewsForReportId: compose.effect(async (e: { reportId: string }) =>
    savedViewsByReportId({ reportId: e.reportId })
      .then((payload) => payload.map(createSavedView))
      .then((savedViews) => savedViews.sort((a, b) => a.title.localeCompare(b.title)))
      .then((savedViews) => {
        loadSavedViewsSuccess(savedViews);
        return savedViews;
      })
      .catch(() => {
        loadSavedViewsError();
        return [];
      })
  ),
  createSavedView: compose.statefulEffect(
    createStructuredSelector({
      currentEntity: selectCurrentEntity,
      currentReport: selectCurrentReport,
      currentReportConfiguration: selectCurrentReportConfiguration
    }),
    async (
      { currentEntity, currentReport, currentReportConfiguration },
      savedViewTileAndDescription: ISavedViewTitleAndDescription
    ) => {
      // Dirty config edits null the id; persist before create so we never POST null.
      // Callers (e.g. multi-dataset save-as-new) may already pass a persisted id. See BUD-17949.
      let reportConfigurationId: string | undefined;
      try {
        reportConfigurationId = await resolveReportConfigurationId(
          currentReportConfiguration,
          savedViewTileAndDescription.reportConfiguration
        );
      } catch (error) {
        // Persisting the dirty config failed (e.g. 403), and createReportConfiguration already
        // surfaced a permission-specific toast. Tag the error so the modal handler's generic
        // create-failed catch doesn't overwrite (mask) that more helpful message. See BUD-17949.
        throw Object.assign(error instanceof Error ? error : new Error(String(error)), {
          reportConfigurationPersistFailed: true
        });
      }
      const savedView: ISavedView = {
        ...savedViewTileAndDescription,
        id: '',
        entityId: currentEntity.entity_id,
        reportId: currentReport.id,
        reportConfiguration: reportConfigurationId
      };
      let createdView;

      return apiCreateSavedView(savedView)
        .then((sv) => {
          SegmentEffects.track({ eventName: 'Report: View: CreateView', props: sv }).catch((_ignore) => null);
          if (!(currentReport && currentReport.isDimensional)) {
            createdView = sv;
            queueSavedViewEmbed(createSavedView(sv));
          }

          return sv;
        })
        .finally(() => {
          if (!(currentReport && currentReport.isDimensional)) {
            dequeueSavedViewEmbed(createdView);
          }
        })
        .catch((error) => {
          throw error;
        });
    }
  ),
  updateSavedView: compose.effect(
    async (e: {
      savedView: ISavedView;
      updateSiblingViews?: boolean;
      setAsDefault?: boolean;
      metaData?: { chartType: AccountSummaryChartType | EReportChartType; reportIsMultiDataset: boolean };
    }) => {
      const sv = await updateSavedViewById({
        savedViewId: e.savedView.id,
        newSavedView: { ...e.savedView, ...(e.setAsDefault !== undefined && { setAsDefault: e.setAsDefault }) },
        updateSiblingViews: e.updateSiblingViews ?? false
      });
      void syncEmbedBySavedViewId({ savedViewId: sv.id });
      SegmentEffects.track({ eventName: 'Report: View: UpdateView', props: { ...sv, ...e.metaData } }).catch(
        () => null
      );
      return sv;
    }
  ),

  deleteSavedView: compose.effect(async (e: { savedView: ISavedView }) => {
    return deleteSavedViewById({ savedViewId: e.savedView.id }).then(() => {
      reportHistoryActionCreators.clearReportHistory();
      savedViewActionCreators.selectSavedView(null);
    });
  }),
  createPreviewEmbedMigration: compose.effect(async (sv: ISavedView) => {
    queueSavedViewEmbed(sv);
    return createSavedViewPreview({
      title: sv.title,
      description: sv.description,
      reportId: sv.reportId,
      savedViewId: sv.id
    })
      .then(async ({ data }: { data: any }) => {
        dequeueSavedViewEmbed(sv);
        const nsv = produce(sv, (newSv) => {
          newSv.previewEmbedId = data.id;
        });
        return updateSavedViewById({ savedViewId: sv.id, newSavedView: nsv }).then((_ignore) => {
          void savedViewEffects.loadSavedViewsForReportId(sv);
        });
      })
      .catch((error) => {
        dequeueSavedViewEmbed(sv);
        return error;
      });
  }),
  savedViewsReportConfigurationsUsingExtendedColumns: compose.statefulPayloadlessEffect(
    selectSavedViews,
    async (savedViews): Promise<boolean> => {
      const queries = savedViews
        .map((sv) => sv.reportConfiguration) // reportConfigurationId
        .map(async (rcId) => {
          const reportConfiguration = await getReportConfigurationById(rcId);

          return isUsingExtendedColumns(reportConfiguration);
        });

      return Promise.all(queries).then((results) => !results.some((result) => result));
    }
  )
}));
